/**
 * GamesHub — "The Games Wall."
 *
 * One complete screenful on landscape surfaces (touch TVs, tablets on
 * stands) per docs/TOUCH-TV-CONTRACT.md: a slim editorial masthead column
 * on the left (greeting + the Quiet Hours corner + credit), and a 4×3
 * picture-wall of the twelve games filling the remaining space — like the
 * framed-pictures wall of a well-loved den. Every tile is one huge tap
 * target; nothing lives below the fold.
 *
 * Phone portrait (≤430px) falls back to a scrolling single-column stack —
 * phones are personal, held devices, so scroll is fine there.
 */
import type { CSSProperties } from 'react';
import { Link } from 'react-router';

import { usePreferences } from '@/preferences';
import { RevealedImage, buildSrcSet, variantOf } from '@/juice/RevealedImage';

import './GamesHub.css';

/** Width variants emitted by the asset pipeline for hub thumbnails. */
const THUMB_WIDTHS = [340, 680] as const;
/** `<img sizes>` — full-bleed on phone, half on small tablets, wall tile above. */
const THUMB_SIZES = '(max-width: 430px) 100vw, (max-width: 1023px) 50vw, 25vw';

interface Game {
  slug: string;
  name: string;
  /** Path to mount when the player taps the card. */
  href: string;
  thumbnailLight: string;
  thumbnailDark: string;
  /** Off the main wall; listed on the `/coming-soon` shelf instead. The game's
   * own routes keep working either way. */
  comingSoon?: boolean;
}

/** Every game, reading order. Quiet Hours lives in the masthead's Quiet
 * Corner, not on the wall — it is an experience, not a game. */
const GAMES: readonly Game[] = [
  {
    slug: 'trivia',
    name: 'Trivia',
    href: '/trivia',
    thumbnailLight: '/generated/games-hub/thumb-trivia-light.png',
    thumbnailDark: '/generated/games-hub/thumb-trivia-dark.png',
  },
  {
    slug: 'categories',
    name: 'Categories',
    href: '/categories',
    thumbnailLight: '/generated/games-hub/thumb-categories-light.png',
    thumbnailDark: '/generated/games-hub/thumb-categories-dark.png',
    comingSoon: true,
  },
  {
    slug: 'five',
    name: 'Five',
    href: '/five',
    thumbnailLight: '/generated/games-hub/thumb-five-light.png',
    thumbnailDark: '/generated/games-hub/thumb-five-dark.png',
  },
  {
    slug: 'sudoku',
    name: 'Sudoku',
    href: '/sudoku',
    thumbnailLight: '/generated/games-hub/thumb-sudoku-light.png',
    thumbnailDark: '/generated/games-hub/thumb-sudoku-dark.png',
  },
  {
    slug: 'word-search',
    name: 'Word Search',
    href: '/word-search',
    thumbnailLight: '/generated/games-hub/thumb-word-search-light.png',
    thumbnailDark: '/generated/games-hub/thumb-word-search-dark.png',
  },
  {
    slug: 'balloons',
    name: 'Balloon Pop',
    href: '/play/balloons',
    thumbnailLight: '/generated/games-hub/thumb-balloons-light.png',
    thumbnailDark: '/generated/games-hub/thumb-balloons-dark.png',
  },
  {
    slug: 'wheel',
    name: 'The Wheel',
    href: '/wheel',
    thumbnailLight: '/generated/games-hub/thumb-wheel-light.png',
    thumbnailDark: '/generated/games-hub/thumb-wheel-dark.png',
  },
  {
    slug: 'solitaire',
    name: 'Solitaire',
    href: '/solitaire',
    thumbnailLight: '/generated/games-hub/thumb-solitaire-light.png',
    thumbnailDark: '/generated/games-hub/thumb-solitaire-dark.png',
    comingSoon: true,
  },
  {
    slug: 'crossword',
    name: 'Crossword',
    href: '/crossword',
    thumbnailLight: '/generated/games-hub/thumb-crossword-light.png',
    thumbnailDark: '/generated/games-hub/thumb-crossword-dark.png',
    comingSoon: true,
  },
  {
    slug: 'mahjong',
    name: 'Mahjong',
    href: '/mahjong',
    thumbnailLight: '/generated/games-hub/thumb-mahjong-light.png',
    thumbnailDark: '/generated/games-hub/thumb-mahjong-dark.png',
    comingSoon: true,
  },
  {
    slug: 'bingo',
    name: 'Bingo',
    href: '/bingo',
    thumbnailLight: '/generated/games-hub/thumb-bingo-light.png',
    thumbnailDark: '/generated/games-hub/thumb-bingo-dark.png',
  },
  {
    slug: 'atlas',
    name: 'Atlas',
    href: '/atlas',
    thumbnailLight: '/generated/games-hub/thumb-atlas-light.png',
    thumbnailDark: '/generated/games-hub/thumb-atlas-dark.png',
    comingSoon: true,
  },
];

const QUIET_HOURS = {
  name: 'Quiet Hours',
  href: '/quiet-hours',
  thumbnailLight: '/generated/games-hub/thumb-quiet-hours-light.png',
  thumbnailDark: '/generated/games-hub/thumb-quiet-hours-dark.png',
};

function greeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning.';
  if (h < 17) return 'Good afternoon.';
  return 'Good evening.';
}

/** Desktop wall column count — keep in sync with `.games-hub__list`. */
const WALL_COLUMNS = 4;

interface GamesHubProps {
  /** `main` is the wall residents see; `coming-soon` holds the games that are
   * reachable by URL but not yet on that wall. */
  shelf?: 'main' | 'coming-soon';
}

export function GamesHub({ shelf = 'main' }: GamesHubProps) {
  const { theme } = usePreferences();
  const isComingSoon = shelf === 'coming-soon';
  const games = GAMES.filter((game) => Boolean(game.comingSoon) === isComingSoon);
  const wallRows = Math.ceil(games.length / WALL_COLUMNS);
  const quietThumb =
    theme === 'dark' ? QUIET_HOURS.thumbnailDark : QUIET_HOURS.thumbnailLight;

  return (
    <div className="games-hub" data-theme={theme}>
      <aside className="games-hub__masthead">
        <div className="games-hub__masthead-top">
          <p className="games-hub__kicker">The games room</p>
          <h2 className="games-hub__title">{isComingSoon ? 'Coming soon.' : greeting()}</h2>
          <p className="games-hub__subtitle">
            {isComingSoon
              ? 'Games still in the workshop. Have an early look.'
              : 'Pick whatever sounds like a fun half hour.'}
          </p>
        </div>

        <Link
          to={QUIET_HOURS.href}
          className="games-hub__quiet"
          aria-label="Open Quiet Hours — a calm break, not a game"
        >
          <div className="games-hub__quiet-thumb" aria-hidden="true">
            <RevealedImage
              src={quietThumb}
              sources={[
                { type: 'image/avif', srcSet: buildSrcSet(quietThumb, 'avif', THUMB_WIDTHS) },
                { type: 'image/webp', srcSet: buildSrcSet(quietThumb, 'webp', THUMB_WIDTHS) },
                { type: 'image/avif', srcSet: variantOf(quietThumb, 'avif') },
                { type: 'image/webp', srcSet: variantOf(quietThumb, 'webp') },
              ]}
              sizes="(max-width: 430px) 100vw, 340px"
              width={1024}
              height={768}
              alt=""
              eager
            />
          </div>
          <span className="games-hub__quiet-text">
            <span className="games-hub__quiet-name">{QUIET_HOURS.name}</span>
            <span className="games-hub__quiet-blurb">A calm break, when you want one</span>
          </span>
        </Link>

        <p className="games-hub__credit">Made with care by Rendever Games</p>
      </aside>

      <nav
        className="games-hub__wall"
        aria-label={isComingSoon ? 'Games coming soon' : 'Available games'}
      >
        <ol
          className="games-hub__list"
          role="list"
          style={{ '--games-hub-rows': wallRows } as CSSProperties}
        >
          {games.map((game) => {
            const thumb = theme === 'dark' ? game.thumbnailDark : game.thumbnailLight;
            return (
              <li key={game.slug} className="games-hub__item">
                <Link
                  to={game.href}
                  className="games-hub__tile"
                  aria-label={`Play ${game.name}`}
                >
                  <div className="games-hub__thumb" aria-hidden="true">
                    <RevealedImage
                      src={thumb}
                      sources={[
                        { type: 'image/avif', srcSet: buildSrcSet(thumb, 'avif', THUMB_WIDTHS) },
                        { type: 'image/webp', srcSet: buildSrcSet(thumb, 'webp', THUMB_WIDTHS) },
                        { type: 'image/avif', srcSet: variantOf(thumb, 'avif') },
                        { type: 'image/webp', srcSet: variantOf(thumb, 'webp') },
                      ]}
                      sizes={THUMB_SIZES}
                      width={1024}
                      height={768}
                      alt=""
                      eager
                    />
                  </div>
                  <span className="games-hub__scrim" aria-hidden="true" />
                  <span className="games-hub__plaque">
                    <span className="games-hub__game-name">{game.name}</span>
                    <span className="games-hub__play" aria-hidden="true">
                      Play
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}
